import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getTokenAuth } from '../../utils/authUtils';
import apiClient from '../../services/api';

const StripeConnectOnboarding = () => {
  const { t } = useTranslation('financial');
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testAmount, setTestAmount] = useState('10.00');

  // Étapes du processus d'onboarding
  const steps = [
    {
      label: t('stripeConnect.steps.createAccount', 'Créer le compte Stripe'),
      description: t('stripeConnect.steps.createAccountDesc', 'Création de votre compte de paiement sécurisé')
    },
    {
      label: t('stripeConnect.steps.completeInfo', 'Compléter les informations'),
      description: t('stripeConnect.steps.completeInfoDesc', 'Remplir vos informations bancaires et personnelles')
    },
    {
      label: t('stripeConnect.steps.verification', 'Vérification'),
      description: t('stripeConnect.steps.verificationDesc', 'Validation de votre compte par Stripe')
    },
    {
      label: t('stripeConnect.steps.activated', 'Compte activé'),
      description: t('stripeConnect.steps.activatedDesc', 'Prêt à recevoir des paiements')
    }
  ];

  useEffect(() => {
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/stripe-connect/account-status');
      
      console.log('🔍 [STRIPE DEBUG] Account status response:', response.data);
      
      if (response.data.success || response.data.data) {
        const data = response.data.success ? response.data.data : response.data;
        console.log('🔍 [STRIPE DEBUG] Account status data:', data);
        setAccountStatus(data);
        updateActiveStep(data);
      } else {
        throw new Error(response.data.message || 'Erreur de statut');
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      toast.error(t('stripeConnect.actions.statusCheckError', 'Erreur lors de la vérification du statut du compte'));
    } finally {
      setLoading(false);
    }
  };

  const updateActiveStep = (status) => {
    console.log('🔍 [STRIPE DEBUG] Updating active step with status:', status);
    console.log('🔍 [STRIPE DEBUG] hasAccount:', status.hasAccount);
    console.log('🔍 [STRIPE DEBUG] onboardingStatus:', status.onboardingStatus);
    console.log('🔍 [STRIPE DEBUG] payoutsEnabled:', status.payoutsEnabled);
    
    if (!status.hasAccount) {
      console.log('🔍 [STRIPE DEBUG] Setting step to 0 (no account)');
      setActiveStep(0);
    } else if (status.onboardingStatus === 'not_started' || 
               status.onboardingStatus === 'in_progress' || 
               status.onboardingStatus === 'failed') {
      console.log('🔍 [STRIPE DEBUG] Setting step to 1 (onboarding needed)');
      setActiveStep(1);
    } else if (status.onboardingStatus === 'completed' && !status.payoutsEnabled) {
      console.log('🔍 [STRIPE DEBUG] Setting step to 2 (verification)');
      setActiveStep(2);
    } else if (status.payoutsEnabled) {
      console.log('🔍 [STRIPE DEBUG] Setting step to 3 (activated)');
      setActiveStep(3);
    }
  };

  const createAccount = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.post('/stripe-connect/create-account', {
        firstName: '', // Sera récupéré du profil utilisateur
        lastName: ''
      });

      if (response.data.success) {
        toast.success(t('stripeConnect.actions.createAccountSuccess', 'Compte Stripe créé avec succès'));
        await checkAccountStatus();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      toast.error(t('stripeConnect.actions.createAccountError', 'Erreur lors de la création du compte Stripe'));
    } finally {
      setLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setLoading(true);
      const refreshUrl = window.location.href;
      const returnUrl = `${window.location.origin}/financial-center?onboarding=success`;

      const response = await apiClient.post('/stripe-connect/create-account-link', {
        refreshUrl,
        returnUrl
      });

      if (response.data.success || response.data.url) {
        const url = response.data.success ? response.data.data.url : response.data.url;
        // Rediriger vers Stripe
        window.location.href = url;
      } else {
        throw new Error(response.data.message || 'Erreur lors de la création du lien');
      }
    } catch (error) {
      console.error('Erreur lors du démarrage de l\'onboarding:', error);
      toast.error(t('stripeConnect.actions.onboardingError', 'Erreur lors du démarrage de l\'onboarding'));
    } finally {
      setLoading(false);
    }
  };

  const createTestTransfer = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post('/stripe-connect/test-transfer', {
        amount: parseFloat(testAmount),
        currency: 'eur',
        description: 'Test de transfert depuis Krillou'
      });

      if (response.data.success) {
        toast.success(t('stripeConnect.testDialog.successMsg', { amount: testAmount, defaultValue: `Transfert test de ${testAmount}€ créé avec succès` }));
        setShowTestDialog(false);
      } else {
        throw new Error(response.data.message || 'Erreur lors du transfert');
      }
    } catch (error) {
      console.error('Erreur lors du transfert test:', error);
      toast.error(t('stripeConnect.actions.testTransferError', 'Erreur lors du transfert test'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'enabled': return 'success';
      case 'pending': return 'warning';
      case 'restricted': 
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enabled': return <CheckIcon />;
      case 'pending': return <WarningIcon />;
      case 'restricted': 
      case 'failed': return <ErrorIcon />;
      default: return <BankIcon />;
    }
  };

  const getConnectStatusLabel = (status) => {
    if (!status) return t('stripeConnect.notCreated', 'Non créé');
    return t(`stripeConnect.connectStatus.${status}`, status);
  };

  const getOnboardingStatusLabel = (status) => {
    if (!status) return t('stripeConnect.notStarted', 'Non démarré');
    return t(`stripeConnect.onboardingStatus.${status}`, status);
  };

  if (loading && !accountStatus) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('stripeConnect.title', 'Configuration des Paiements Stripe')}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('stripeConnect.subtitle', 'Configurez votre compte Stripe Connect pour recevoir automatiquement vos revenus de location.')}
      </Typography>

      {/* Statut actuel */}
      {accountStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="h6">{t('stripeConnect.accountStatus', 'Statut du compte')}</Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={checkAccountStatus}
                disabled={loading}
              >
                {t('stripeConnect.refresh', 'Actualiser')}
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{t('stripeConnect.connectAccount', 'Compte Connect')}:</Typography>
                  <Chip
                    icon={getStatusIcon(accountStatus.connectStatus)}
                    label={getConnectStatusLabel(accountStatus.connectStatus)}
                    color={getStatusColor(accountStatus.connectStatus)}
                    size="small"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{t('stripeConnect.onboarding', 'Onboarding')}:</Typography>
                  <Chip
                    label={getOnboardingStatusLabel(accountStatus.onboardingStatus)}
                    color={getStatusColor(accountStatus.onboardingStatus)}
                    size="small"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{t('stripeConnect.payoutsEnabled', 'Paiements activés')}:</Typography>
                  <Chip
                    label={accountStatus.payoutsEnabled ? t('stripeConnect.yes', 'Oui') : t('stripeConnect.no', 'Non')}
                    color={accountStatus.payoutsEnabled ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              
              {accountStatus.payoutsEnabled && (
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{t('stripeConnect.stripeConnectLabel', 'Stripe Connect')}:</Typography>
                    <Chip
                      label={accountStatus.stripeConnectEnabled ? t('stripeConnect.activated', 'Activé') : t('stripeConnect.deactivated', 'Désactivé')}
                      color={accountStatus.stripeConnectEnabled ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Alertes selon le statut */}
            {accountStatus.connectStatus === 'restricted' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {accountStatus.onboardingStatus === 'failed' ? (
                  <>
                    {t('stripeConnect.restriction.failedOnboarding', 'Votre onboarding a échoué ou est incomplet. Cliquez sur "Compléter l\'onboarding" pour reprendre le processus.')}
                  </>
                ) : (
                  <>
                    {t('stripeConnect.restriction.needsInfo', 'Votre compte nécessite des informations supplémentaires. Cliquez sur "Compléter l\'onboarding" pour continuer.')}
                  </>
                )}
              </Alert>
            )}

            {accountStatus.requirements && 
             accountStatus.requirements.currently_due && 
             accountStatus.requirements.currently_due.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {t('stripeConnect.requiredInfoTitle', 'Informations à compléter :')}
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                  {accountStatus.requirements.currently_due.map((field) => (
                    <Box component="li" key={field}>
                      <Typography variant="body2">
                        {t(`stripeConnect.requiredFields.${field.replace(/\./g, '_')}`, field)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Étapes d'onboarding */}
      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Typography variant="body2" paragraph>
                    {step.description}
                  </Typography>
                  
                  {index === 0 && !accountStatus?.hasAccount && (
                    <Button
                      variant="contained"
                      onClick={createAccount}
                      disabled={loading}
                      sx={{ mt: 1 }}
                    >
                      {loading ? <CircularProgress size={20} /> : t('stripeConnect.actions.createAccount', 'Créer le compte')}
                    </Button>
                  )}
                  
                  {index === 1 && accountStatus?.hasAccount && !accountStatus?.payoutsEnabled && (
                    <Button
                      variant="contained"
                      onClick={startOnboarding}
                      disabled={loading}
                      sx={{ mt: 1 }}
                    >
                      {loading ? <CircularProgress size={20} /> : t('stripeConnect.actions.completeOnboarding', 'Compléter l\'onboarding')}
                    </Button>
                  )}
                  
                  {index === 3 && accountStatus?.payoutsEnabled && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {t('stripeConnect.successMessage', '🎉 Félicitations ! Votre compte est configuré et prêt à recevoir des paiements.')}
                      </Alert>
                      
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          variant="outlined"
                          onClick={() => setShowTestDialog(true)}
                          sx={{ mt: 1 }}
                        >
                          {t('stripeConnect.actions.testTransfer', 'Tester un transfert')}
                        </Button>
                      )}
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Dialog de test de transfert */}
      <Dialog open={showTestDialog} onClose={() => setShowTestDialog(false)}>
        <DialogTitle>{t('stripeConnect.testDialog.title', 'Test de transfert')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {t('stripeConnect.testDialog.description', 'Créer un transfert test vers votre compte Stripe Connect.')}
          </Typography>
          <TextField
            label={t('stripeConnect.testDialog.amountLabel', 'Montant (€)')}
            value={testAmount}
            onChange={(e) => setTestAmount(e.target.value)}
            type="number"
            inputProps={{ min: 1, max: 100, step: 0.01 }}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)}>
            {t('stripeConnect.testDialog.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={createTestTransfer}
            variant="contained"
            disabled={loading || !testAmount}
          >
            {loading ? <CircularProgress size={20} /> : t('stripeConnect.testDialog.submit', 'Créer le transfert')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StripeConnectOnboarding;
